/**
 * TOSS Web Application — Client Portal messenger API (`/api/client/messenger/chats/*`)
 *
 * A company holds several chats: the conversation with ТОСС, groups it shares with us, and chats
 * its own people create among themselves. Every route names the one it acts on.
 *
 * <b>Naming a chat is safe here for a reason that is not in this file.</b> The ban on `chatId`
 * (decision Р30) protected a tenancy identifier that was derivable from the session — while there
 * was one chat, naming it could only ever be an attempt to name somebody else's. With several, the
 * guarantee moved to the server: one resolver stands in front of every route below and answers 404
 * for another company's chat, 404 for one this account was taken out of, and 403 only for a write
 * inside a conversation the caller belongs to. `customerId` and `userId` remain forbidden.
 *
 * Contract: vault/api/client/06-messenger.md.
 */

import apiClient from '@/api/client';
import { CLIENT_API_BASE } from './clientPortal.types';
import type {
  ClientChatAvatarResponse,
  ClientChatDto,
  ClientChatListResponse,
  ClientChatSummaryDto,
  CreateClientChatRequest,
  ClientChatMemberDto,
  ClientChatMessageDto,
  ClientMessageContextResponse,
  ClientMessageReactionDto,
  ClientPinnedMessageDto,
  ClientMessageListResponse,
  ClientMessageSearchRequest,
  ClientMessageSearchResponse,
  ClientMessagesRequest,
  EditClientMessageRequest,
  PostApplicationCardRequest,
  ShareClientFileRequest,
  SendClientMessageRequest,
} from './clientPortal.types';
import type { ChatDeleteScope } from '@/utils/chatDeleteDialog';

// ============================================================================
// Endpoints
// ============================================================================

const BASE = `${CLIENT_API_BASE}/messenger/chats`;

const ENDPOINTS = {
  CHATS: BASE,
  chat: (chatId: string) => `${BASE}/${chatId}`,
  avatar: (chatId: string) => `${BASE}/${chatId}/avatar`,
  pin: (chatId: string) => `${BASE}/${chatId}/pin`,
  mute: (chatId: string) => `${BASE}/${chatId}/mute`,
  leave: (chatId: string) => `${BASE}/${chatId}/leave`,
  messages: (chatId: string) => `${BASE}/${chatId}/messages`,
  message: (chatId: string, messageId: string) => `${BASE}/${chatId}/messages/${messageId}`,
  messageContext: (chatId: string, messageId: string) =>
    `${BASE}/${chatId}/messages/${messageId}/context`,
  search: (chatId: string) => `${BASE}/${chatId}/messages/search`,
  cards: (chatId: string) => `${BASE}/${chatId}/cards`,
  shareFile: (chatId: string) => `${BASE}/${chatId}/messages/share-file`,
  files: (chatId: string) => `${BASE}/${chatId}/files`,
  readAll: (chatId: string) => `${BASE}/${chatId}/read-all`,
  pinMessage: (chatId: string) => `${BASE}/${chatId}/pin-message`,
  forward: (chatId: string) => `${BASE}/${chatId}/messages/forward`,
  reactions: (chatId: string, messageId: string) =>
    `${BASE}/${chatId}/messages/${messageId}/reactions`,
  reaction: (chatId: string, messageId: string, emoji: string) =>
    `${BASE}/${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
  members: (chatId: string) => `${BASE}/${chatId}/members`,
  membersRemove: (chatId: string) => `${BASE}/${chatId}/members/remove`,
} as const;

// ============================================================================
// Reads
// ============================================================================

/**
 * The company's chat with ТОСС. Creates it on first call (idempotently), so this is also the
 * screen's "is there anything here yet" probe.
 *
 * `title` is always the literal «ТОСС» for this audience; `unreadCount` is the customer's own.
 */
export async function getClientChats(search?: string): Promise<ClientChatListResponse> {
  const response = await apiClient.get<ClientChatListResponse>(ENDPOINTS.CHATS, {
    params: search ? { search } : undefined,
  });
  return response.data;
}

/** One chat's card: title, unread count, read state. */
export async function getClientChat(chatId: string): Promise<ClientChatDto> {
  const response = await apiClient.get<ClientChatDto>(ENDPOINTS.chat(chatId));
  return response.data;
}

/**
 * Create a chat inside the customer's own company: a named group, or a one-to-one chat with one
 * colleague. Portal owner only, and only while the server accepts creation at all.
 *
 * @throws 403 `CLIENT_NOT_PORTAL_OWNER` / `CLIENT_CHAT_CREATION_DISABLED`;
 * 400 `CLIENT_CHAT_LIMIT_REACHED`; 404 for an account that is not this company's.
 */
export async function createClientChat(
  body: CreateClientChatRequest
): Promise<ClientChatSummaryDto> {
  const response = await apiClient.post<ClientChatSummaryDto>(ENDPOINTS.CHATS, body);
  return response.data;
}

/**
 * Delete the chat for the caller alone (`scope=me`) or for every participant, permanently
 * (`scope=everyone`, no recycle bin — Owner decision 2026-09-23). The server always refuses both
 * on the conversation with ТОСС (`CHAT_NOT_DELETABLE`).
 */
export async function deleteClientChat(chatId: string, scope: ChatDeleteScope): Promise<void> {
  await apiClient.delete(ENDPOINTS.chat(chatId), { params: { scope } });
}

/** Rename a group of the customer's own. */
export async function renameClientChat(chatId: string, title: string): Promise<void> {
  await apiClient.patch(ENDPOINTS.chat(chatId), { title });
}

/** Set a group's avatar. Sends BYTES — the server mints the stored name itself. */
export async function uploadClientChatAvatar(
  chatId: string,
  file: File
): Promise<ClientChatAvatarResponse> {
  const form = new FormData();
  form.append('file', file);

  const response = await apiClient.post<ClientChatAvatarResponse>(ENDPOINTS.avatar(chatId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/** Pin the chat to the top of THIS person's list, or unpin it. */
export async function setClientChatPinned(chatId: string, isPinned: boolean): Promise<void> {
  if (isPinned) {
    await apiClient.post(ENDPOINTS.pin(chatId));
  } else {
    await apiClient.delete(ENDPOINTS.pin(chatId));
  }
}

/**
 * Silence the chat for this person, or unsilence it. The unread count is unaffected — silencing is
 * about not being interrupted, not about pretending the conversation was read.
 */
export async function setClientChatMuted(chatId: string, isMuted: boolean): Promise<void> {
  if (isMuted) {
    await apiClient.post(ENDPOINTS.mute(chatId));
  } else {
    await apiClient.delete(ENDPOINTS.mute(chatId));
  }
}

/** Leave a group of the customer's own. Refused for the chat with ТОСС and for a private chat. */
export async function leaveClientChat(chatId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.leave(chatId));
}

/**
 * One page of messages, newest first, cursor-paged like the internal messenger.
 *
 * Pass `nextCursor` from the previous response to load older messages; omit `cursor` for the
 * first page.
 */
export async function getClientMessages(
  chatId: string,
  params?: ClientMessagesRequest
): Promise<ClientMessageListResponse> {
  const response = await apiClient.get<ClientMessageListResponse>(ENDPOINTS.messages(chatId), {
    params,
  });
  return response.data;
}

// ============================================================================
// Writes
// ============================================================================

/**
 * Send a message. Returns the created message, ready to append optimistically.
 *
 * There is no `messageType` in the body: the server sets `File` when `fileId` is present and
 * `Text` otherwise. Upload the file first with `uploadClientFile(file, { chatId })` — a `fileId`
 * that is not stamped with this chat and this company is rejected.
 *
 * @throws 400 `EMPTY_MESSAGE` when neither `content` nor `fileId` is present.
 */
export async function sendClientMessage(
  chatId: string,
  body: SendClientMessageRequest
): Promise<ClientChatMessageDto> {
  const response = await apiClient.post<ClientChatMessageDto>(ENDPOINTS.messages(chatId), body);
  return response.data;
}

/**
 * Post one of the company's applications into the chat as a card.
 *
 * The server builds both the human-readable `content` (mandatory — the Flutter app does not know
 * `MessageType.Card` and shows the text instead) and the structured `payload`. The client sends
 * nothing but the id.
 *
 * @throws 404 for another company's application; 409 `CARD_ALREADY_POSTED` when it is already in
 * the chat.
 */
export async function postClientApplicationCard(
  chatId: string,
  body: PostApplicationCardRequest
): Promise<ClientChatMessageDto> {
  const response = await apiClient.post<ClientChatMessageDto>(ENDPOINTS.cards(chatId), body);
  return response.data;
}

/**
 * Post a photo or video the company already sees (an application's attachment, a ПТО's or a
 * container's inspection media) into one of its chats — «Переслать в чат», one message per chat,
 * the optional comment as its text. Name, type and size come from the server's own record.
 *
 * @throws 404 for a file the company cannot open; the chat checks are those of any send.
 */
export async function shareClientFile(
  chatId: string,
  body: ShareClientFileRequest
): Promise<ClientChatMessageDto> {
  const response = await apiClient.post<ClientChatMessageDto>(ENDPOINTS.shareFile(chatId), body);
  return response.data;
}

/**
 * Search the conversation. Substring matching over message text AND attachment names, newest hit
 * first, with `totalCount` for the «N из M» counter.
 *
 * @throws 400 when `query` is shorter than two characters.
 */
export async function searchClientMessages(
  chatId: string,
  params: ClientMessageSearchRequest
): Promise<ClientMessageSearchResponse> {
  const response = await apiClient.get<ClientMessageSearchResponse>(ENDPOINTS.search(chatId), {
    params,
  });
  return response.data;
}

/**
 * The window around one message, oldest first — what «jump to the quoted message» loads when the
 * target is older than every page already on screen.
 *
 * @throws 404 when the message is not in this company's chat.
 */
export async function getClientMessageContext(
  chatId: string,
  messageId: string,
  surroundingCount?: number
): Promise<ClientMessageContextResponse> {
  const response = await apiClient.get<ClientMessageContextResponse>(
    ENDPOINTS.messageContext(chatId, messageId),
    { params: surroundingCount === undefined ? undefined : { surroundingCount } }
  );
  return response.data;
}

/** Every attachment of the conversation, newest first, cursor-paged like the message list. */
export async function getClientChatFiles(
  chatId: string,
  params?: ClientMessagesRequest
): Promise<ClientMessageListResponse> {
  const response = await apiClient.get<ClientMessageListResponse>(ENDPOINTS.files(chatId), {
    params,
  });
  return response.data;
}

/**
 * Rewrite the text of one's OWN message. The server refuses somebody else's (403 `ACCESS_DENIED`)
 * and refuses a card or a file bubble, whose text is not the sender's to correct.
 *
 * @throws 403 `ACCESS_DENIED`; 400 `MESSAGE_DELETED`; 404 when it is not in this chat.
 */
export async function editClientMessage(
  chatId: string,
  messageId: string,
  body: EditClientMessageRequest
): Promise<ClientChatMessageDto> {
  const response = await apiClient.put<ClientChatMessageDto>(
    ENDPOINTS.message(chatId, messageId),
    body
  );
  return response.data;
}

/**
 * Soft-delete one's own message: it becomes «Сообщение удалено» for both sides, because it is one
 * row. Answers 204.
 *
 * @throws 403 `ACCESS_DENIED` for somebody else's message.
 */
export async function deleteClientMessage(chatId: string, messageId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.message(chatId, messageId));
}

/** Mark the whole chat read. Answers 204 — there is no body to read. */
export async function markClientChatRead(chatId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.readAll(chatId));
}

/**
 * Who takes part in the conversation — the ТОСС side first, then every portal account of the
 * caller's own company, in the chat or not.
 */
export async function getClientChatMembers(chatId: string): Promise<ClientChatMemberDto[]> {
  const { data } = await apiClient.get<ClientChatMemberDto[]>(ENDPOINTS.members(chatId));
  return data;
}

/**
 * Switch a colleague back into the conversation. Portal owner only.
 *
 * The account travels in the BODY, never in the path: `ClientApiContractTests` forbids a tenancy
 * identifier as a route or query parameter, and `POST /api/client/users/active-company` set the
 * precedent for putting one in a form instead.
 *
 * @throws 403 `CLIENT_NOT_PORTAL_OWNER`, 404 for an account that is not this company's.
 */
export async function addClientChatMember(
  chatId: string,
  userId: string
): Promise<ClientChatMemberDto> {
  const { data } = await apiClient.post<ClientChatMemberDto>(ENDPOINTS.members(chatId), { userId });
  return data;
}

/**
 * Switch a colleague out of the conversation. Portal owner only, and never oneself — the owner is
 * the only account that can put anybody back.
 *
 * A POST to `.../members/remove` rather than `DELETE .../members/{userId}` for the same reason the
 * add takes a body: the id may not appear in a route on this surface.
 *
 * @throws 403 `CLIENT_NOT_PORTAL_OWNER`, 400 when targeting oneself.
 */
export async function removeClientChatMember(chatId: string, userId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.membersRemove(chatId), { userId });
}

// ============================================================================
// Namespace Export
// ============================================================================

/**
 * Client messenger API namespace.
 *
 * @example
 * ```ts
 * import { clientMessengerApi } from '@/api/clientPortal/clientMessenger.api';
 *
 * const chat = await clientMessengerApi.getChat();
 * ```
 */
/**
 * Ставит эмодзи на сообщение. Отвечает ПОЛНЫМ набором реакций этого сообщения — не дельтой, —
 * поэтому результат можно класть в кэш как есть, не пересчитывая счётчики на клиенте.
 *
 * Повторное нажатие той же эмодзи сервер считает идемпотентным: набор вернётся прежним.
 */
export async function addClientMessageReaction(
  chatId: string,
  messageId: string,
  emoji: string
): Promise<ClientMessageReactionDto[]> {
  const response = await apiClient.post<ClientMessageReactionDto[]>(
    ENDPOINTS.reactions(chatId, messageId),
    { emoji }
  );
  return response.data;
}

/** Снимает СВОЮ эмодзи. Чужие остаются — сервер удаляет строку только этого пользователя. */
export async function removeClientMessageReaction(
  chatId: string,
  messageId: string,
  emoji: string
): Promise<ClientMessageReactionDto[]> {
  const response = await apiClient.delete<ClientMessageReactionDto[]>(
    ENDPOINTS.reaction(chatId, messageId, emoji)
  );
  return response.data;
}

/** Закрепляет сообщение переписки. Одно на чат: новое закрепление заменяет прежнее. */
export async function pinClientMessage(
  chatId: string,
  messageId: string
): Promise<ClientPinnedMessageDto> {
  const response = await apiClient.post<ClientPinnedMessageDto>(ENDPOINTS.pinMessage(chatId), {
    messageId,
  });
  return response.data;
}

/** Снимает закрепление. Снять то, чего нет, — не ошибка. */
export async function unpinClientMessage(chatId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.pinMessage(chatId));
}

/**
 * Пересылает сообщения в чат `chatId` — целевой, а не исходный.
 *
 * Отвечает созданными сообщениями в хронологическом порядке (сначала пересланные, затем
 * приписка). Сервер откажет 400, если источник не может ехать в эту переписку.
 */
export async function forwardClientMessages(
  chatId: string,
  messageIds: string[],
  comment?: string
): Promise<ClientChatMessageDto[]> {
  const response = await apiClient.post<ClientChatMessageDto[]>(ENDPOINTS.forward(chatId), {
    messageIds,
    comment,
  });
  return response.data;
}

export const clientMessengerApi = {
  getChats: getClientChats,
  getChat: getClientChat,
  createChat: createClientChat,
  deleteChat: deleteClientChat,
  renameChat: renameClientChat,
  uploadAvatar: uploadClientChatAvatar,
  setPinned: setClientChatPinned,
  setMuted: setClientChatMuted,
  leaveChat: leaveClientChat,
  getMessages: getClientMessages,
  searchMessages: searchClientMessages,
  getMessageContext: getClientMessageContext,
  getFiles: getClientChatFiles,
  sendMessage: sendClientMessage,
  editMessage: editClientMessage,
  deleteMessage: deleteClientMessage,
  postApplicationCard: postClientApplicationCard,
  shareFile: shareClientFile,
  markRead: markClientChatRead,
  getMembers: getClientChatMembers,
  addMember: addClientChatMember,
  removeMember: removeClientChatMember,
  addReaction: addClientMessageReaction,
  removeReaction: removeClientMessageReaction,
  pinMessage: pinClientMessage,
  unpinMessage: unpinClientMessage,
  forwardMessages: forwardClientMessages,
} as const;
